import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    CheckCircle2,
    AlertTriangle,
    XCircle,
    Minus,
    RefreshCw,
    ArrowRight,
    Undo2,
    Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import * as YearEndService from '../../../../services/YearEndService';
import { READINESS_CHECK_INFO } from '../constants';

// Icon color + badge tint per check status — Bootstrap theme vars for the
// icons, the shared ye-badge palette (YearEndPage chrome) for the labels.
const STATUS_META = {
    pass: { Icon: CheckCircle2, color: 'var(--bs-success)', badge: 'success', label: 'Klart' },
    warn: { Icon: AlertTriangle, color: 'var(--bs-warning)', badge: 'warning', label: 'Kontrollera' },
    fail: { Icon: XCircle, color: 'var(--bs-danger)', badge: 'danger', label: 'Behöver åtgärdas' },
    not_applicable: { Icon: Minus, color: '#9ca3af', badge: 'neutral', label: 'Gäller inte dig' },
};

const MAX_DETAIL_ITEMS = 10;

/** Human-readable rendering of one entry in a details array — the
 *  backend sends different shapes per check (periods, verifikat, counts),
 *  so we render defensively. */
function formatDetailItem(item) {
    if (item == null) return null;
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    if (typeof item === 'object') {
        if (item.label) return item.label;
        if (item.title) return item.title;
        if (item.name) return item.name;
        if (item.description) return item.description;
        // VAT periods: {start_date, end_date, ...}
        if (item.start_date && item.end_date) return `${item.start_date} – ${item.end_date}`;
        // Verifikat: {series, number} or {verification_number}
        if (item.series != null && item.number != null) return `${item.series}${item.number}`;
        if (item.verification_number != null) return `Verifikat ${item.verification_number}`;
        if (item.verification_id != null) return `Verifikat ${item.verification_id}`;
        // Open fiscal years: {fiscal_year_id, year, end_date}
        if (item.year != null && item.end_date != null) {
            return `Räkenskapsår ${item.year} (t.o.m. ${item.end_date})`;
        }
        if (item.id != null) return String(item.id);
        return JSON.stringify(item);
    }
    return String(item);
}

/** Renderer for a check's `details` payload. Only user-meaningful content is
 *  shown: a per-check sentence (READINESS_CHECK_INFO.detailText) and any
 *  lists (öppna perioder, obalanserade verifikat). Raw scalar fields like
 *  endDate/state are machine diagnostics and are never printed as key: value. */
function CheckDetails({ check, info }) {
    const details = check.details;
    if (details == null) return null;

    const sentence = typeof info.detailText === 'function' ? info.detailText(details) : null;

    let lists = [];
    if (Array.isArray(details)) {
        lists = [[null, details]];
    } else if (typeof details === 'object') {
        Object.entries(details).forEach(([key, value]) => {
            if (Array.isArray(value)) lists.push([key, value]);
        });
    }

    if (!sentence && lists.every(([, arr]) => arr.length === 0)) return null;

    return (
        <div className="ye-well">
            {sentence && <div>{sentence}</div>}
            {lists.map(([key, arr]) => arr.length > 0 && (
                <ul key={key || 'list'}>
                    {arr.slice(0, MAX_DETAIL_ITEMS).map((item, i) => (
                        <li key={i}>{formatDetailItem(item)}</li>
                    ))}
                    {arr.length > MAX_DETAIL_ITEMS && (
                        <li className="text-muted">… och {arr.length - MAX_DETAIL_ITEMS} till</li>
                    )}
                </ul>
            ))}
        </div>
    );
}

/**
 * Step 1 — Förberedelser: the pre-flight gate. Renders the readiness
 * checks with plain-Swedish explanations, deep links to the fixing
 * surfaces, a re-run button and the "Markera som klart" gate action.
 * Markup is the modern-card composition (header band + body + footer
 * band); the shared ye-* chrome comes from YearEndPage.
 */
const PreflightStep = ({
    companyId,
    readiness,
    onRunChecks,
    runningChecks,
    completed,
    onComplete,
    onUndo,
    busy,
    gateError,
}) => {
    const checks = readiness?.checks || [];
    const fiscalYearId = readiness?.fiscalYear?.fiscal_year_id || readiness?.fiscalYear?.id;
    const [repairing, setRepairing] = useState(false);

    const handleRepair = async () => {
        setRepairing(true);
        try {
            const resp = await YearEndService.repairPlResidual(companyId, fiscalYearId);
            const v = resp?.data?.verification;
            toast.success(`Rättelseverifikatet är bokfört${v?.verificationNumber ? ` (${v.series || 'V'}${v.verificationNumber})` : ''}.`);
            onRunChecks();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Reparationen kunde inte genomföras.');
        } finally {
            setRepairing(false);
        }
    };

    return (
        <>
            <style>{`
                .ye-check-row {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.9rem;
                    padding: 1rem 0;
                    border-bottom: 1px solid #f3f4f6;
                }
                .ye-check-row:first-child { padding-top: 0; }
                .ye-check-row:last-child { border-bottom: none; padding-bottom: 0; }
                .ye-check-icon { flex-shrink: 0; margin-top: 2px; }
                .ye-check-title {
                    font-weight: 600;
                    font-size: 0.875rem;
                    color: var(--title, #374151);
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    flex-wrap: wrap;
                }
                .ye-check-explanation {
                    font-size: 0.8125rem;
                    color: #6b7280;
                    margin-top: 0.25rem;
                    line-height: 1.5;
                    max-width: 62ch;
                }
                .ye-check-link {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    font-size: 0.8125rem;
                    font-weight: 600;
                    margin-top: 0.4rem;
                    color: var(--primary);
                }
                .ye-check-link:hover { color: var(--primary-hover, var(--primary)); }
            `}</style>

            <div className="modern-card-header ye-step-header">
                <div>
                    <h4 className="section-title">Förberedelser</h4>
                    <p className="section-subtitle">
                        Innan bokslutet kan börja behöver bokföringen vara komplett. Vi kontrollerar
                        de vanligaste sakerna åt dig — punkter som inte är klara har en länk till
                        sidan där du fixar dem.
                    </p>
                </div>
                <div className="action-buttons">
                    <button
                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={onRunChecks}
                        disabled={runningChecks}
                    >
                        <RefreshCw size={14} className={runningChecks ? 'spin' : ''} />
                        {runningChecks ? 'Kontrollerar…' : 'Kör kontrollerna igen'}
                    </button>
                </div>
            </div>

            <div className="modern-card-body">
                {checks.length === 0 ? (
                    <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                        Inga kontroller att visa ännu. Klicka på ”Kör kontrollerna igen”.
                    </p>
                ) : (
                    checks.map((check) => {
                        const meta = STATUS_META[check.status] || STATUS_META.not_applicable;
                        const info = READINESS_CHECK_INFO[check.key] || {};
                        const needsAction = check.status === 'warn' || check.status === 'fail';
                        const { Icon } = meta;

                        return (
                            <div className="ye-check-row" key={check.key}>
                                <span className="ye-check-icon" style={{ color: meta.color }}>
                                    <Icon size={20} />
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="ye-check-title">
                                        {check.title}
                                        <span className={`ye-badge ye-badge-${meta.badge}`}>
                                            {meta.label}
                                        </span>
                                    </div>
                                    {(check.status === 'not_applicable'
                                        ? (info.naExplanation || info.explanation)
                                        : info.explanation) && (
                                        <div className="ye-check-explanation">
                                            {check.status === 'not_applicable'
                                                ? (info.naExplanation || info.explanation)
                                                : info.explanation}
                                        </div>
                                    )}
                                    {needsAction && check.key !== 'ledger_integrity' && (
                                        <CheckDetails check={check} info={info} />
                                    )}
                                    {needsAction && info.link && (
                                        <Link className="ye-check-link" to={info.link(companyId)}>
                                            {info.linkLabel || 'Åtgärda'}
                                            <ArrowRight size={14} />
                                        </Link>
                                    )}
                                    {check.key === 'ledger_integrity' && needsAction && (
                                        <div style={{ marginTop: '0.5rem' }}>
                                            {(check.details?.issues || []).map((issue) => (
                                                <div className="ye-check-explanation" key={issue.key}>
                                                    {issue.message}
                                                </div>
                                            ))}
                                            {(check.details?.priorYearIssues || []).map((issue) => (
                                                <div className="ye-check-explanation" key={`${issue.year}-${issue.key}`}>
                                                    {issue.year}: {issue.message}
                                                </div>
                                            ))}
                                            {(check.details?.issues || []).some((i) => i.repairable) && (
                                                <button
                                                    className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2 mt-2"
                                                    onClick={handleRepair}
                                                    disabled={repairing}
                                                >
                                                    <Wrench size={14} />
                                                    {repairing ? 'Reparerar…' : 'Reparera med rättelseverifikat'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}

                {gateError && (
                    <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                        <strong>{gateError.message}</strong>
                        {gateError.failingChecks && gateError.failingChecks.length > 0 && (
                            <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem' }}>
                                {gateError.failingChecks.map((c, i) => (
                                    <li key={i}>{c.title || c.key || formatDetailItem(c)}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <div className="ye-step-footer">
                {completed ? (
                    <>
                        <span className="ye-footer-done">
                            <CheckCircle2 size={18} style={{ color: 'var(--bs-success)' }} />
                            Förberedelserna är markerade som klara
                        </span>
                        <button
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                            onClick={onUndo}
                            disabled={busy}
                        >
                            <Undo2 size={14} />
                            Ångra markeringen
                        </button>
                    </>
                ) : (
                    <>
                        <span className="ye-footer-hint">
                            {readiness?.ready
                                ? 'Allt ser klart ut — du kan gå vidare.'
                                : 'Åtgärda punkterna ovan och kör kontrollerna igen.'}
                        </span>
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={onComplete}
                            disabled={busy}
                        >
                            {busy ? 'Sparar…' : 'Markera som klart'}
                            <ArrowRight size={16} />
                        </button>
                    </>
                )}
            </div>
        </>
    );
};

export default PreflightStep;
